import { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { Lock, Loader2, LogOut, Search, CheckCircle2, AlertTriangle, RefreshCw, Trophy, Camera, CameraOff, Download, Plus } from "lucide-react";
import { toast } from "sonner";
import { ADMIN } from "@/constants/testIds";
import { BrandLockup } from "@/components/Logo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "urbx_admin_token";

export default function Admin() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || "");
  const [password, setPassword] = useState("");
  const [logging, setLogging] = useState(false);

  if (!token) {
    const onLogin = async (e) => {
      e.preventDefault();
      setLogging(true);
      try {
        const { data } = await axios.post(`${API}/admin/login`, { password });
        localStorage.setItem(TOKEN_KEY, data.token);
        setToken(data.token);
      } catch (err) {
        toast.error(err?.response?.data?.detail || "Connexion refusée");
      } finally {
        setLogging(false);
      }
    };

    return (
      <div className="min-h-screen bg-[#14230A] text-[#FDF0DF] flex items-center justify-center px-5">
        <form onSubmit={onLogin} className="w-full max-w-md">
          <div className="flex justify-center mb-8">
            <BrandLockup inverse />
          </div>
          <div className="border-2 border-[#FDF0DF] p-8">
            <div className="font-cond uppercase tracking-[0.3em] text-[11px] opacity-60 flex items-center gap-2">
              <Lock className="w-3.5 h-3.5" /> Admin · Bar URBX
            </div>
            <h1 className="font-display text-4xl mt-2">Accès staff</h1>
            <label className="block mt-6 font-cond uppercase tracking-[0.2em] text-[11px]">
              Mot de passe
            </label>
            <input
              data-testid={ADMIN.loginPasswordInput}
              type="password"
              required
              autoFocus
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="mt-2 w-full h-12 px-3 bg-transparent border-2 border-[#FDF0DF] focus:border-[#FF4D00] outline-none font-cond text-base"
            />
            <button
              data-testid={ADMIN.loginBtn}
              type="submit"
              disabled={logging}
              className="mt-5 w-full h-12 bg-[#FF4D00] text-[#FDF0DF] font-cond uppercase tracking-[0.2em] font-bold flex items-center justify-center gap-2"
            >
              {logging ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
              {logging ? "Connexion…" : "Entrer"}
            </button>
          </div>
        </form>
      </div>
    );
  }

  return <AdminDashboard token={token} onLogout={() => { localStorage.removeItem(TOKEN_KEY); setToken(""); }} />;
}

function AdminDashboard({ token, onLogout }) {
  const [tab, setTab] = useState("dashboard");
  const [stats, setStats] = useState(null);
  const [orders, setOrders] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  const headers = { "X-Admin-Token": token };

  const refresh = async () => {
    setLoading(true);
    try {
      const [s, o, p] = await Promise.all([
        axios.get(`${API}/admin/stats`, { headers }),
        axios.get(`${API}/admin/orders`, { headers }),
        axios.get(`${API}/admin/products`, { headers }),
      ]);
      setStats(s.data);
      setOrders(o.data);
      setProducts(p.data);
    } catch (err) {
      if (err?.response?.status === 401) {
        toast.error("Session expirée");
        onLogout();
      } else {
        toast.error("Erreur de chargement");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let cancelled = false;
    queueMicrotask(() => {
      if (!cancelled) refresh();
    });
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div className="min-h-screen bg-[#FDF0DF] text-[#14230A]">
      <header className="border-b-2 border-[#14230A] bg-[#14230A] text-[#FDF0DF]">
        <div className="max-w-[1440px] mx-auto px-5 md:px-10 h-16 flex items-center justify-between">
          <BrandLockup inverse />
          <div className="flex items-center gap-2">
            <button
              onClick={refresh}
              className="h-10 w-10 border-2 border-[#FDF0DF] inline-flex items-center justify-center hover:bg-[#FF4D00] hover:border-[#FF4D00]"
              aria-label="Rafraîchir"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
            </button>
            <button
              data-testid={ADMIN.logoutBtn}
              onClick={onLogout}
              className="h-10 px-3 border-2 border-[#FDF0DF] inline-flex items-center gap-2 font-cond uppercase tracking-[0.2em] text-xs hover:bg-[#FF4D00] hover:border-[#FF4D00]"
            >
              <LogOut className="w-3.5 h-3.5" /> Déco
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-[1440px] mx-auto px-5 md:px-10 py-8">
        <div className="flex items-end justify-between">
          <h1 className="font-display text-4xl md:text-6xl">Pilotage URBX</h1>
        </div>

        <div className="mt-6 flex gap-2 flex-wrap border-b-2 border-[#14230A]">
          {[
            { id: "dashboard", l: "Dashboard" },
            { id: "audience", l: "Audience" },
            { id: "orders", l: "Commandes" },
            { id: "products", l: "Stock" },
            { id: "scan", l: "Scan / Validation" },
            { id: "wheel", l: "Roue · Tirages" },
            { id: "sponsors", l: "Sponsors B2B" },
          ].map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={`px-4 h-11 font-cond uppercase tracking-[0.2em] text-xs -mb-0.5 border-b-2 ${
                tab === t.id ? "border-[#FF4D00] text-[#FF4D00]" : "border-transparent hover:border-[#14230A]"
              }`}
            >
              {t.l}
            </button>
          ))}
        </div>

        <div className="mt-8">
          {tab === "dashboard" && <Dashboard stats={stats} loading={loading} />}
          {tab === "audience" && <Audience headers={headers} />}
          {tab === "orders" && <Orders orders={orders} token={token} />}
          {tab === "products" && <Products products={products} headers={headers} onChange={refresh} />}
          {tab === "scan" && <Scan headers={headers} onRedeem={refresh} />}
          {tab === "wheel" && <WheelAdmin headers={headers} />}
          {tab === "sponsors" && <SponsorsB2B headers={headers} token={token} />}
        </div>
      </div>
    </div>
  );
}

function Dashboard({ stats, loading }) {
  const topProducts = useMemo(() => {
    if (!stats) return [];
    return Object.entries(stats.by_product || {}).sort((a, b) => b[1].qty - a[1].qty);
  }, [stats]);

  if (!stats) return <div className="text-[#14230A]/60">Chargement…</div>;
  return (
    <div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <StatCard testid="admin-stats-revenue" label="Chiffre d'affaires" value={`${stats.revenue_eur.toFixed(2)} €`} accent />
        <StatCard testid="admin-stats-orders" label="Commandes" value={stats.orders_count} />
        <StatCard testid="admin-stats-items" label="Consommations" value={stats.items_sold} />
      </div>

      <div className="mt-10">
        <h3 className="font-display text-3xl">Top ventes</h3>
        <div className="mt-4 border-2 border-[#14230A]">
          <table className="w-full text-left">
            <thead className="bg-[#14230A] text-[#FDF0DF]">
              <tr>
                <th className="px-4 py-3 font-cond uppercase tracking-[0.15em] text-xs">Produit</th>
                <th className="px-4 py-3 font-cond uppercase tracking-[0.15em] text-xs">Quantité</th>
                <th className="px-4 py-3 font-cond uppercase tracking-[0.15em] text-xs text-right">CA</th>
              </tr>
            </thead>
            <tbody>
              {topProducts.map(([id, row]) => (
                <tr key={id} className="border-t border-[#14230A]/20">
                  <td className="px-4 py-3 font-cond">{row.name}</td>
                  <td className="px-4 py-3 font-cond">{row.qty}</td>
                  <td className="px-4 py-3 font-cond text-right">{row.revenue.toFixed(2)} €</td>
                </tr>
              ))}
              {topProducts.length === 0 && (
                <tr>
                  <td colSpan={3} className="px-4 py-6 text-center text-[#14230A]/60">
                    Aucune vente pour le moment.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function StatCard({ label, value, accent = false, testid }) {
  return (
    <div
      data-testid={testid}
      className={`border-2 border-[#14230A] p-5 ${accent ? "bg-[#14230A] text-[#FDF0DF]" : ""}`}
    >
      <div className={`font-cond uppercase tracking-[0.25em] text-[11px] ${accent ? "opacity-70" : "opacity-60"}`}>
        {label}
      </div>
      <div className={`font-display text-5xl mt-1 ${accent ? "text-[#FF4D00]" : ""}`}>{value}</div>
    </div>
  );
}

function Audience({ headers }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/analytics`, { headers });
      setData(data);
    } catch {
      toast.error("Erreur chargement audience");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let cancelled = false;
    queueMicrotask(() => {
      if (!cancelled) load();
    });
    return () => { cancelled = true; };
  }, []);

  if (loading && !data) return <div className="text-[#14230A]/60">Chargement…</div>;
  if (!data) return null;

  const maxBar = Math.max(1, ...data.timeseries_14d.map((d) => d.pageviews));

  return (
    <div data-testid="admin-audience">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-display text-3xl">Audience</h3>
        <button
          onClick={load}
          className="h-10 px-3 border-2 border-[#14230A] inline-flex items-center gap-2 font-cond uppercase tracking-[0.2em] text-xs hover:bg-[#14230A] hover:text-[#FDF0DF]"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} /> Rafraîchir
        </button>
      </div>

      {/* KPIs principaux */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard testid="kpi-visitors-all" label="Visiteurs uniques" value={data.unique_visitors.all} accent />
        <StatCard testid="kpi-visitors-24h" label="Visiteurs 24h" value={data.unique_visitors["24h"]} />
        <StatCard testid="kpi-pageviews-all" label="Pages vues" value={data.pageviews.all} />
        <StatCard testid="kpi-conversion" label="Taux de conversion" value={`${data.conversion_rate_pct} %`} />
      </div>

      {/* Funnel */}
      <div className="mt-8">
        <h4 className="font-cond uppercase tracking-[0.25em] text-xs opacity-70 mb-3">Entonnoir de conversion (tout temps)</h4>
        <FunnelBar
          steps={[
            { label: "Visiteurs uniques", value: data.unique_visitors.all },
            { label: "Checkout démarré", value: data.checkout_started.all },
            { label: "Commandes payées", value: data.orders_paid.all },
          ]}
        />
      </div>

      {/* Timeseries 14j */}
      <div className="mt-10">
        <h4 className="font-cond uppercase tracking-[0.25em] text-xs opacity-70 mb-3">14 derniers jours · pages vues / visiteurs</h4>
        <div className="border-2 border-[#14230A] p-4">
          <div className="flex items-end gap-1 h-40">
            {data.timeseries_14d.map((d) => {
              const h = Math.round((d.pageviews / maxBar) * 100);
              return (
                <div key={d.date} className="flex-1 flex flex-col items-center justify-end" title={`${d.date} · ${d.pageviews} vues · ${d.visitors} visiteurs`}>
                  <div
                    className="w-full bg-[#FF4D00]"
                    style={{ height: `${h}%`, minHeight: d.pageviews > 0 ? "2px" : "0" }}
                  />
                </div>
              );
            })}
          </div>
          <div className="mt-2 flex justify-between font-cond uppercase tracking-[0.2em] text-[10px] opacity-60">
            <span>{data.timeseries_14d[0]?.date}</span>
            <span>{data.timeseries_14d[data.timeseries_14d.length - 1]?.date}</span>
          </div>
        </div>
      </div>

      {/* Top paths + referrers */}
      <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <h4 className="font-cond uppercase tracking-[0.25em] text-xs opacity-70 mb-3">Top pages (30 jours)</h4>
          <div className="border-2 border-[#14230A]">
            <table className="w-full text-left text-sm">
              <thead className="bg-[#14230A] text-[#FDF0DF]">
                <tr>
                  <th className="px-3 py-2 font-cond uppercase tracking-[0.15em] text-xs">Page</th>
                  <th className="px-3 py-2 font-cond uppercase tracking-[0.15em] text-xs text-right">Vues</th>
                </tr>
              </thead>
              <tbody>
                {data.top_paths.length === 0 ? (
                  <tr><td colSpan={2} className="px-3 py-4 text-center opacity-60">Aucune donnée</td></tr>
                ) : data.top_paths.map((p) => (
                  <tr key={p.path} className="border-t border-[#14230A]/20">
                    <td className="px-3 py-2 font-mono">{p.path}</td>
                    <td className="px-3 py-2 text-right">{p.views}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        <div>
          <h4 className="font-cond uppercase tracking-[0.25em] text-xs opacity-70 mb-3">Sources de trafic (30 jours)</h4>
          <div className="border-2 border-[#14230A]">
            <table className="w-full text-left text-sm">
              <thead className="bg-[#14230A] text-[#FDF0DF]">
                <tr>
                  <th className="px-3 py-2 font-cond uppercase tracking-[0.15em] text-xs">Source</th>
                  <th className="px-3 py-2 font-cond uppercase tracking-[0.15em] text-xs text-right">Vues</th>
                </tr>
              </thead>
              <tbody>
                {data.top_referrers.length === 0 ? (
                  <tr><td colSpan={2} className="px-3 py-4 text-center opacity-60">Aucune donnée</td></tr>
                ) : data.top_referrers.map((r) => (
                  <tr key={r.source} className="border-t border-[#14230A]/20">
                    <td className="px-3 py-2">{r.source}</td>
                    <td className="px-3 py-2 text-right">{r.views}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <p className="mt-6 text-xs opacity-50">
        Tracker maison · anonyme · pas de cookies. Les visiteurs uniques sont basés sur un identifiant local généré dans le navigateur. L&apos;admin n&apos;est pas tracké.
      </p>
    </div>
  );
}

function FunnelBar({ steps }) {
  const max = Math.max(1, ...steps.map((s) => s.value));
  return (
    <div className="space-y-2">
      {steps.map((s, i) => {
        const pct = Math.round((s.value / max) * 100);
        return (
          <div key={s.label}>
            <div className="flex justify-between font-cond uppercase tracking-[0.15em] text-[11px]">
              <span>{s.label}</span>
              <span className="opacity-60">
                {s.value}
                {i > 0 && steps[0].value > 0 && (
                  <span className="ml-2">· {Math.round((s.value / steps[0].value) * 100)}%</span>
                )}
              </span>
            </div>
            <div className="mt-1 h-6 border-2 border-[#14230A] bg-[#FDF0DF]">
              <div className="h-full bg-[#14230A]" style={{ width: `${pct}%` }} />
            </div>
          </div>
        );
      })}
    </div>
  );
}


function Orders({ orders, token }) {
  const downloadCsv = () => {
    const url = `${API}/admin/orders.csv?token=${encodeURIComponent(token)}`;
    window.open(url, "_blank");
  };
  return (
    <div className="space-y-3">
      <div className="flex justify-end">
        <button
          data-testid="orders-download-csv"
          onClick={downloadCsv}
          className="h-10 px-4 bg-[#14230A] text-[#FDF0DF] font-cond uppercase tracking-[0.2em] text-xs inline-flex items-center gap-2 hover:bg-[#FF4D00]"
        >
          <Download className="w-3.5 h-3.5" /> Export CSV compta
        </button>
      </div>
      <div data-testid={ADMIN.ordersTable} className="border-2 border-[#14230A] overflow-x-auto">
      <table className="w-full text-left min-w-[700px]">
        <thead className="bg-[#14230A] text-[#FDF0DF]">
          <tr>
            <th className="px-3 py-3 font-cond uppercase tracking-[0.15em] text-xs">Réf.</th>
            <th className="px-3 py-3 font-cond uppercase tracking-[0.15em] text-xs">Client</th>
            <th className="px-3 py-3 font-cond uppercase tracking-[0.15em] text-xs">Email</th>
            <th className="px-3 py-3 font-cond uppercase tracking-[0.15em] text-xs">Items</th>
            <th className="px-3 py-3 font-cond uppercase tracking-[0.15em] text-xs text-right">Total</th>
            <th className="px-3 py-3 font-cond uppercase tracking-[0.15em] text-xs">Statut</th>
          </tr>
        </thead>
        <tbody>
          {orders.length === 0 && (
            <tr>
              <td colSpan={6} className="px-4 py-6 text-center text-[#14230A]/60">
                Aucune commande encore.
              </td>
            </tr>
          )}
          {orders.map((o) => (
            <tr key={o.booking_ref} className="border-t border-[#14230A]/20">
              <td className="px-3 py-2 font-display text-base">{o.booking_ref}</td>
              <td className="px-3 py-2">{o.customer_name}</td>
              <td className="px-3 py-2 text-xs text-[#14230A]/70">{o.customer_email}</td>
              <td className="px-3 py-2 text-xs">
                {o.items.map((it) => `${it.name}×${it.quantity}`).join(", ")}
              </td>
              <td className="px-3 py-2 text-right font-cond font-bold">{o.total_eur.toFixed(2)} €</td>
              <td className="px-3 py-2">
                <span
                  className={`px-2 py-0.5 font-cond uppercase text-[10px] tracking-[0.15em] ${
                    o.redeemed ? "bg-[#14230A] text-[#FDF0DF]" : "bg-[#FF4D00] text-[#FDF0DF]"
                  }`}
                >
                  {o.redeemed ? "Servi" : "À servir"}
                </span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
    </div>
  );
}

function Products({ products, headers, onChange }) {
  const [editing, setEditing] = useState({});
  const [busy, setBusy] = useState(null);
  const [showNew, setShowNew] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);

  const patchProduct = async (id, patch) => {
    setBusy(id);
    try {
      await axios.patch(`${API}/admin/products/${id}`, patch, { headers });
      toast.success("Mis à jour");
      setEditing((s) => {
        const next = { ...s };
        delete next[id];
        return next;
      });
      setEditingProduct(null);
      onChange();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erreur");
    } finally {
      setBusy(null);
    }
  };

  const deleteProduct = async (id, name) => {
    if (!window.confirm(`Supprimer "${name}" du catalogue ? Cette action est définitive.`)) return;
    setBusy(id);
    try {
      await axios.delete(`${API}/admin/products/${id}`, { headers });
      toast.success(`${name} supprimé`);
      onChange();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erreur");
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center flex-wrap gap-2">
        <div className="font-cond uppercase tracking-[0.3em] text-xs opacity-60">
          {products.length} produits · clique sur une cellule pour éditer
        </div>
        <button
          data-testid="product-new-btn"
          onClick={() => setShowNew(true)}
          className="h-10 px-4 bg-[#FF4D00] text-[#FDF0DF] font-cond uppercase tracking-[0.2em] text-xs font-bold inline-flex items-center gap-2"
        >
          <Plus className="w-4 h-4" /> Nouveau produit
        </button>
      </div>

      <div data-testid={ADMIN.productsTable} className="border-2 border-[#14230A] overflow-x-auto">
        <table className="w-full text-left min-w-[900px]">
          <thead className="bg-[#14230A] text-[#FDF0DF]">
            <tr>
              <th className="px-3 py-3 font-cond uppercase tracking-[0.15em] text-xs">Produit</th>
              <th className="px-3 py-3 font-cond uppercase tracking-[0.15em] text-xs">Catégorie</th>
              <th className="px-3 py-3 font-cond uppercase tracking-[0.15em] text-xs">Prix</th>
              <th className="px-3 py-3 font-cond uppercase tracking-[0.15em] text-xs">Initial</th>
              <th className="px-3 py-3 font-cond uppercase tracking-[0.15em] text-xs">Restant</th>
              <th className="px-3 py-3 font-cond uppercase tracking-[0.15em] text-xs">Vendu</th>
              <th className="px-3 py-3 font-cond uppercase tracking-[0.15em] text-xs">Actions</th>
            </tr>
          </thead>
          <tbody>
            {products.map((p) => {
              const sold = p.stock_initial - p.stock_remaining;
              const pct = p.stock_initial > 0 ? Math.round((sold / p.stock_initial) * 100) : 0;
              const e = editing[p.id] || {};
              const isPack = p.is_pack;
              const isDeposit = p.is_deposit || p.id === "ecocup";
              const remVal = e.stock_remaining ?? p.stock_remaining;
              const initVal = e.stock_initial ?? p.stock_initial;
              const priceVal = e.price_eur ?? p.price_eur;
              const dirty = e.stock_remaining !== undefined
                          || e.stock_initial !== undefined
                          || e.price_eur !== undefined;
              return (
                <tr key={p.id} className="border-t border-[#14230A]/20 align-middle">
                  <td className="px-3 py-2 font-cond font-bold">
                    {p.name}
                    {p.stock_remaining === 0 && (
                      <span className="ml-2 text-[10px] uppercase tracking-widest bg-[#C53A1B] text-[#FDF0DF] px-1.5 py-0.5">SOLD OUT</span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-xs uppercase tracking-widest text-[#14230A]/70">{p.category}</td>
                  <td className="px-3 py-2">
                    <input
                      type="number"
                      step="0.01"
                      min={0}
                      disabled={isPack}
                      value={priceVal}
                      onChange={(ev) => setEditing((s) => ({ ...s, [p.id]: { ...(s[p.id] || {}), price_eur: ev.target.value } }))}
                      className="w-20 h-8 px-2 border-2 border-[#14230A] bg-transparent font-cond disabled:opacity-50"
                    />
                  </td>
                  <td className="px-3 py-2">
                    <input
                      type="number"
                      min={0}
                      disabled={isPack || isDeposit}
                      value={initVal}
                      onChange={(ev) => setEditing((s) => ({ ...s, [p.id]: { ...(s[p.id] || {}), stock_initial: ev.target.value } }))}
                      className="w-20 h-8 px-2 border-2 border-[#14230A] bg-transparent font-cond disabled:opacity-50"
                    />
                  </td>
                  <td className="px-3 py-2">
                    <input
                      type="number"
                      min={0}
                      value={remVal}
                      onChange={(ev) => setEditing((s) => ({ ...s, [p.id]: { ...(s[p.id] || {}), stock_remaining: ev.target.value } }))}
                      className={`w-20 h-8 px-2 border-2 bg-transparent font-cond ${p.stock_remaining === 0 ? "border-[#C53A1B] text-[#C53A1B] font-bold" : "border-[#14230A]"}`}
                    />
                  </td>
                  <td className="px-3 py-2 text-sm">
                    {sold} ({pct}%)
                  </td>
                  <td className="px-3 py-2">
                    <div className="inline-flex items-center gap-1">
                      <button
                        disabled={!dirty || busy === p.id}
                        onClick={() => {
                          const patch = {};
                          if (e.price_eur !== undefined) patch.price_eur = parseFloat(e.price_eur);
                          if (e.stock_initial !== undefined) patch.stock_initial = parseInt(e.stock_initial);
                          if (e.stock_remaining !== undefined) patch.stock_remaining = parseInt(e.stock_remaining);
                          patchProduct(p.id, patch);
                        }}
                        className="h-8 px-3 bg-[#14230A] text-[#FDF0DF] font-cond uppercase text-[10px] tracking-widest disabled:opacity-30"
                      >
                        Sauver
                      </button>
                      <button
                        type="button"
                        onClick={() => setEditingProduct(p)}
                        title="Modifier nom / image / description"
                        className="h-8 w-8 border-2 border-[#14230A] inline-flex items-center justify-center hover:bg-[#14230A] hover:text-[#FDF0DF]"
                      >
                        ✎
                      </button>
                      {!isPack && !isDeposit && (
                        <button
                          type="button"
                          disabled={busy === p.id}
                          onClick={() => deleteProduct(p.id, p.name)}
                          title="Supprimer"
                          className="h-8 w-8 border-2 border-[#C53A1B] text-[#C53A1B] inline-flex items-center justify-center hover:bg-[#C53A1B] hover:text-[#FDF0DF]"
                        >
                          🗑
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {showNew && (
        <NewProductModal
          headers={headers}
          onClose={() => setShowNew(false)}
          onCreated={() => { setShowNew(false); onChange(); }}
        />
      )}
      {editingProduct && (
        <EditProductModal
          product={editingProduct}
          onClose={() => setEditingProduct(null)}
          onSave={(patch) => patchProduct(editingProduct.id, patch)}
          saving={busy === editingProduct.id}
        />
      )}
    </div>
  );
}

function NewProductModal({ headers, onClose, onCreated }) {
  const [form, setForm] = useState({
    id: "", name: "", category: "soft", price_eur: 2, stock_initial: 24,
    unit_label: "canette", is_alcohol: false, description: "", image_url: "",
  });
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await axios.post(`${API}/admin/products`, form, { headers });
      toast.success(`${form.name} créé`);
      onCreated();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erreur");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
      <form onSubmit={submit} className="bg-[#FDF0DF] border-4 border-[#14230A] p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="font-display text-2xl mb-4">Nouveau produit</div>
        <div className="grid grid-cols-2 gap-3 text-sm">
          <label className="col-span-2">
            <div className="font-cond uppercase text-[10px] tracking-widest mb-1">ID (slug)</div>
            <input data-testid="new-product-id" required value={form.id} onChange={(e) => setForm((f) => ({ ...f, id: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "") }))} placeholder="pina-colada" className="w-full h-10 px-2 border-2 border-[#14230A] bg-transparent" />
          </label>
          <label className="col-span-2">
            <div className="font-cond uppercase text-[10px] tracking-widest mb-1">Nom</div>
            <input data-testid="new-product-name" required value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} className="w-full h-10 px-2 border-2 border-[#14230A] bg-transparent" />
          </label>
          <label>
            <div className="font-cond uppercase text-[10px] tracking-widest mb-1">Catégorie</div>
            <select value={form.category} onChange={(e) => setForm((f) => ({ ...f, category: e.target.value }))} className="w-full h-10 px-2 border-2 border-[#14230A] bg-transparent">
              <option value="signature">Signature</option>
              <option value="cocktail">Cocktail</option>
              <option value="beer">Bière</option>
              <option value="soft">Soft</option>
              <option value="energy">Energy</option>
              <option value="juice">Jus</option>
              <option value="water">Eau</option>
              <option value="food">Food</option>
            </select>
          </label>
          <label>
            <div className="font-cond uppercase text-[10px] tracking-widest mb-1">Alcool ?</div>
            <select value={form.is_alcohol ? "1" : "0"} onChange={(e) => setForm((f) => ({ ...f, is_alcohol: e.target.value === "1" }))} className="w-full h-10 px-2 border-2 border-[#14230A] bg-transparent">
              <option value="0">Non</option>
              <option value="1">Oui</option>
            </select>
          </label>
          <label>
            <div className="font-cond uppercase text-[10px] tracking-widest mb-1">Prix (€)</div>
            <input data-testid="new-product-price" type="number" step="0.01" min={0.01} required value={form.price_eur} onChange={(e) => setForm((f) => ({ ...f, price_eur: parseFloat(e.target.value) || 0 }))} className="w-full h-10 px-2 border-2 border-[#14230A] bg-transparent" />
          </label>
          <label>
            <div className="font-cond uppercase text-[10px] tracking-widest mb-1">Stock initial</div>
            <input data-testid="new-product-stock" type="number" min={1} required value={form.stock_initial} onChange={(e) => setForm((f) => ({ ...f, stock_initial: parseInt(e.target.value) || 0 }))} className="w-full h-10 px-2 border-2 border-[#14230A] bg-transparent" />
          </label>
          <label className="col-span-2">
            <div className="font-cond uppercase text-[10px] tracking-widest mb-1">Unité (ex: canette, verre, bouteille)</div>
            <input value={form.unit_label} onChange={(e) => setForm((f) => ({ ...f, unit_label: e.target.value }))} className="w-full h-10 px-2 border-2 border-[#14230A] bg-transparent" />
          </label>
          <label className="col-span-2">
            <div className="font-cond uppercase text-[10px] tracking-widest mb-1">URL image</div>
            <input value={form.image_url} onChange={(e) => setForm((f) => ({ ...f, image_url: e.target.value }))} placeholder="https://..." className="w-full h-10 px-2 border-2 border-[#14230A] bg-transparent" />
          </label>
          <label className="col-span-2">
            <div className="font-cond uppercase text-[10px] tracking-widest mb-1">Description</div>
            <textarea rows={3} value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} className="w-full px-2 py-2 border-2 border-[#14230A] bg-transparent" />
          </label>
        </div>
        <div className="mt-5 flex gap-2 justify-end">
          <button type="button" onClick={onClose} className="h-10 px-4 border-2 border-[#14230A] font-cond uppercase text-xs tracking-widest">Annuler</button>
          <button data-testid="new-product-create" type="submit" disabled={saving} className="h-10 px-4 bg-[#FF4D00] text-[#FDF0DF] font-cond uppercase text-xs tracking-widest font-bold disabled:opacity-50">
            {saving ? "Création..." : "Créer"}
          </button>
        </div>
      </form>
    </div>
  );
}

function EditProductModal({ product, onClose, onSave, saving }) {
  const [name, setName] = useState(product.name);
  const [description, setDescription] = useState(product.description || "");
  const [image_url, setImageUrl] = useState(product.image_url || "");

  const submit = (e) => {
    e.preventDefault();
    const patch = {};
    if (name !== product.name) patch.name = name;
    if (description !== (product.description || "")) patch.description = description;
    if (image_url !== (product.image_url || "")) patch.image_url = image_url;
    if (Object.keys(patch).length === 0) {
      toast.info("Aucun changement");
      onClose();
      return;
    }
    onSave(patch);
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
      <form onSubmit={submit} className="bg-[#FDF0DF] border-4 border-[#14230A] p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="font-display text-2xl mb-1">Modifier le produit</div>
        <div className="font-cond uppercase text-[10px] tracking-widest opacity-60 mb-4">{product.id}</div>
        <div className="space-y-3 text-sm">
          <label className="block">
            <div className="font-cond uppercase text-[10px] tracking-widest mb-1">Nom</div>
            <input value={name} onChange={(e) => setName(e.target.value)} className="w-full h-10 px-2 border-2 border-[#14230A] bg-transparent" />
          </label>
          <label className="block">
            <div className="font-cond uppercase text-[10px] tracking-widest mb-1">URL image</div>
            <input value={image_url} onChange={(e) => setImageUrl(e.target.value)} className="w-full h-10 px-2 border-2 border-[#14230A] bg-transparent" />
            {image_url && <img alt="" src={image_url} className="mt-2 max-h-32 object-contain border-2 border-[#14230A]" />}
          </label>
          <label className="block">
            <div className="font-cond uppercase text-[10px] tracking-widest mb-1">Description</div>
            <textarea rows={3} value={description} onChange={(e) => setDescription(e.target.value)} className="w-full px-2 py-2 border-2 border-[#14230A] bg-transparent" />
          </label>
        </div>
        <div className="mt-5 flex gap-2 justify-end">
          <button type="button" onClick={onClose} className="h-10 px-4 border-2 border-[#14230A] font-cond uppercase text-xs tracking-widest">Annuler</button>
          <button type="submit" disabled={saving} className="h-10 px-4 bg-[#FF4D00] text-[#FDF0DF] font-cond uppercase text-xs tracking-widest font-bold disabled:opacity-50">
            {saving ? "..." : "Sauver"}
          </button>
        </div>
      </form>
    </div>
  );
}

function Scan({ headers }) {
  const [ref, setRef] = useState("");
  const [order, setOrder] = useState(null);
  const [redeemed, setRedeemed] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [scanning, setScanning] = useState(false);
  const scannerRef = useRef(null);
  const containerId = "qr-scanner-region";

  const fetchOrder = async (raw) => {
    // Accept booking_ref OR full URL that contains URBX-XXXXXX pattern
    const match = String(raw || "").toUpperCase().match(/URBX-[A-Z0-9]{4,10}/);
    const refClean = match ? match[0] : String(raw || "").trim().toUpperCase().replace(/[^A-Z0-9-]/g, "");
    if (!refClean) return;
    setError("");
    setOrder(null);
    setRedeemed(false);
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/order/${refClean}`, { headers });
      setOrder(data);
      setRedeemed(!!data.redeemed);
      setRef(refClean);
      if (data.redeemed) {
        toast.error(`${refClean} — déjà servie`);
      } else {
        toast.success(`${refClean} chargée`);
      }
    } catch (err) {
      setError(err?.response?.data?.detail || "Référence introuvable");
    } finally {
      setLoading(false);
    }
  };

  const onSearch = (e) => {
    e.preventDefault();
    fetchOrder(ref);
  };

  const onRedeem = async () => {
    try {
      const { data } = await axios.post(`${API}/admin/order/${order.booking_ref}/redeem`, {}, { headers });
      setOrder(data.order);
      setRedeemed(true);
      toast.success(data.already ? "Déjà servi" : "Validé !");
    } catch {
      toast.error("Erreur");
    }
  };

  const stopScan = async () => {
    if (scannerRef.current) {
      try {
        await scannerRef.current.stop();
      } catch (e) {
        void e;
      }
      try {
        await scannerRef.current.clear();
      } catch (e) {
        void e;
      }
      scannerRef.current = null;
    }
    setScanning(false);
  };

  const startScan = async () => {
    setError("");
    setScanning(true);
    try {
      if (!navigator.mediaDevices || typeof navigator.mediaDevices.getUserMedia !== "function") {
        throw new Error("MediaDevices API indisponible. Ouvre le site en HTTPS sur Chrome ou Safari.");
      }
      const { Html5Qrcode } = await import("html5-qrcode");
      await new Promise((r) => setTimeout(r, 50));
      const scanner = new Html5Qrcode(containerId);
      scannerRef.current = scanner;
      await scanner.start(
        { facingMode: "environment" },
        { fps: 10, qrbox: { width: 250, height: 250 } },
        async (decoded) => {
          await stopScan();
          fetchOrder(decoded);
        },
        () => {}
      );
    } catch (err) {
      const detail = err?.message || "";
      const msg = detail.includes("MediaDevices")
        ? detail
        : "Caméra inaccessible. Autorise l'accès dans ton navigateur (icône 🔒 dans la barre d'URL).";
      setError(msg);
      setScanning(false);
    }
  };

  useEffect(() => {
    return () => {
      stopScan();
    };
  }, []);

  return (
    <div className="max-w-xl space-y-4">
      <div className="flex gap-2 flex-wrap">
        {!scanning ? (
          <button
            data-testid="scan-camera-start"
            onClick={startScan}
            className="h-12 px-5 bg-[#FF4D00] text-[#FDF0DF] font-cond uppercase tracking-[0.2em] text-xs inline-flex items-center gap-2 font-bold"
          >
            <Camera className="w-4 h-4" /> Scanner le QR
          </button>
        ) : (
          <button
            data-testid="scan-camera-stop"
            onClick={stopScan}
            className="h-12 px-5 border-2 border-[#14230A] font-cond uppercase tracking-[0.2em] text-xs inline-flex items-center gap-2"
          >
            <CameraOff className="w-4 h-4" /> Arrêter
          </button>
        )}
      </div>

      <div
        id={containerId}
        className={`${scanning ? "block" : "hidden"} border-2 border-[#14230A] max-w-[360px] aspect-square overflow-hidden`}
      />

      <form onSubmit={onSearch} className="flex gap-2">
        <input
          data-testid={ADMIN.redeemInput}
          value={ref}
          onChange={(e) => setRef(e.target.value)}
          placeholder="URBX-XXXXXX"
          className="flex-1 h-12 px-3 border-2 border-[#14230A] bg-transparent font-cond text-base uppercase tracking-widest"
        />
        <button
          data-testid={ADMIN.redeemBtn}
          type="submit"
          disabled={loading}
          className="h-12 px-5 bg-[#14230A] text-[#FDF0DF] font-cond uppercase tracking-[0.2em] text-xs inline-flex items-center gap-2"
        >
          <Search className="w-4 h-4" /> Chercher
        </button>
      </form>

      {error && (
        <div className="border-2 border-[#C53A1B] p-4 text-[#C53A1B] inline-flex items-center gap-2">
          <AlertTriangle className="w-4 h-4" /> {error}
        </div>
      )}

      {order && (
        <div className="border-2 border-[#14230A] p-5">
          <div className="flex items-center justify-between">
            <div>
              <div className="font-cond uppercase tracking-[0.3em] text-[11px] opacity-60">Commande</div>
              <div className="font-display text-3xl">{order.booking_ref}</div>
            </div>
            {redeemed && (
              <span
                data-testid="order-already-served"
                className="px-2 py-1 bg-[#14230A] text-[#FDF0DF] font-cond uppercase text-[10px] tracking-[0.2em]"
              >
                Déjà utilisé
              </span>
            )}
          </div>
          <div className="mt-2 text-sm">
            {order.customer_name} · {order.customer_email}
          </div>
          <ul className="mt-3 space-y-1 text-sm">
            {order.items.map((it) => (
              <li key={it.product_id} className="flex justify-between">
                <span>{it.name}</span>
                <span className="font-cond font-bold">× {it.quantity}</span>
              </li>
            ))}
          </ul>
          <div className="flex items-baseline justify-between mt-4 pt-3 border-t border-[#14230A]/20">
            <span className="font-cond uppercase tracking-[0.2em] text-sm">Total</span>
            <span className="font-display text-2xl">{order.total_eur.toFixed(2)} €</span>
          </div>

          {redeemed ? (
            <div className="mt-5 bg-[#14230A] text-[#FDF0DF] p-4 inline-flex items-center gap-2">
              <CheckCircle2 className="w-5 h-5 text-[#FF4D00]" /> QR déjà utilisé · commande servie
            </div>
          ) : (
            <button
              data-testid="order-mark-served"
              onClick={onRedeem}
              className="mt-5 w-full h-12 bg-[#FF4D00] text-[#FDF0DF] font-cond uppercase tracking-[0.2em] font-bold"
            >
              ✓ Marquer servie
            </button>
          )}
        </div>
      )}
    </div>
  );
}

function WheelAdmin({ headers }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/wheel/plays`, { headers });
      setData(res.data);
    } catch {
      toast.error("Erreur de chargement");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let cancelled = false;
    queueMicrotask(() => {
      if (!cancelled) load();
    });
    return () => {
      cancelled = true;
    };
  }, []);

  if (loading || !data) return <div className="text-[#14230A]/60">Chargement…</div>;

  const stats = data.stats || {};
  const tiers = [
    { key: "iphone", label: "iPhone 14", color: "#FF4D00" },
    { key: "voucher_15", label: "Bon 15 €", color: "#14230A" },
    { key: "voucher_10", label: "Bon 10 €", color: "#14230A" },
    { key: "voucher_5", label: "Bon 5 €", color: "#14230A" },
    { key: "lose", label: "Perdants", color: "#14230A" },
  ];

  return (
    <div className="space-y-8">
      <div className="grid sm:grid-cols-5 gap-3">
        {tiers.map((t) => {
          const s = stats[t.key] || { total: 0, used: 0 };
          const isWin = t.key !== "lose";
          return (
            <div
              key={t.key}
              className={`border-2 p-4 ${
                t.key === "iphone" ? "border-[#FF4D00] bg-[#FF4D00] text-[#FDF0DF]" : "border-[#14230A]"
              }`}
            >
              <div className={`font-cond uppercase tracking-[0.25em] text-[10px] ${t.key === "iphone" ? "opacity-90" : "opacity-60"}`}>
                {t.label}
              </div>
              <div className={`font-display text-3xl mt-1 ${t.key === "iphone" ? "" : "text-[#14230A]"}`}>
                {s.used}/{s.total}
              </div>
              <div className={`text-[11px] mt-0.5 ${t.key === "iphone" ? "opacity-90" : "opacity-70"}`}>
                {isWin ? "joué / total" : "tirés / total"}
              </div>
            </div>
          );
        })}
      </div>

      <div className="grid sm:grid-cols-2 gap-4">
        <div className="border-2 border-[#14230A] bg-[#14230A] text-[#FDF0DF] p-5">
          <div className="font-cond uppercase tracking-[0.25em] text-[11px] opacity-70">Tirages totaux</div>
          <div className="font-display text-5xl mt-1">{data.total_plays}</div>
        </div>
        <div className="border-2 border-[#FF4D00] bg-[#FF4D00] text-[#FDF0DF] p-5">
          <div className="font-cond uppercase tracking-[0.25em] text-[11px] opacity-90 flex items-center gap-1">
            <Trophy className="w-3 h-3" /> Gagnants
          </div>
          <div className="font-display text-5xl mt-1">{data.winners_count}</div>
        </div>
      </div>

      <div>
        <h3 className="font-display text-3xl mb-3">Derniers tirages</h3>
        <div className="border-2 border-[#14230A] overflow-x-auto">
          <table className="w-full text-left min-w-[800px]">
            <thead className="bg-[#14230A] text-[#FDF0DF]">
              <tr>
                <th className="px-3 py-3 font-cond uppercase tracking-[0.15em] text-xs">Code</th>
                <th className="px-3 py-3 font-cond uppercase tracking-[0.15em] text-xs">Joueur</th>
                <th className="px-3 py-3 font-cond uppercase tracking-[0.15em] text-xs">Email</th>
                <th className="px-3 py-3 font-cond uppercase tracking-[0.15em] text-xs">Réf. cmd</th>
                <th className="px-3 py-3 font-cond uppercase tracking-[0.15em] text-xs">Date</th>
                <th className="px-3 py-3 font-cond uppercase tracking-[0.15em] text-xs">Lot</th>
                <th className="px-3 py-3 font-cond uppercase tracking-[0.15em] text-xs">Preuve</th>
              </tr>
            </thead>
            <tbody>
              {data.plays.length === 0 && (
                <tr>
                  <td colSpan={7} className="px-4 py-6 text-center text-[#14230A]/60">
                    Aucun tirage encore.
                  </td>
                </tr>
              )}
              {data.plays.map((p) => {
                const won = p.prize_type !== "lose";
                return (
                  <tr key={p.code} className="border-t border-[#14230A]/20">
                    <td className="px-3 py-2 font-cond font-bold tracking-widest">
                      {p.code.slice(0, 4)}-{p.code.slice(4)}
                    </td>
                    <td className="px-3 py-2">{p.used_by_name || "—"}</td>
                    <td className="px-3 py-2 text-xs text-[#14230A]/70">{p.used_by_email || "—"}</td>
                    <td className="px-3 py-2 text-xs">{p.used_by_order_ref || "—"}</td>
                    <td className="px-3 py-2 text-xs">
                      {p.used_at ? new Date(p.used_at).toLocaleString("fr-FR") : "—"}
                    </td>
                    <td className="px-3 py-2">
                      {won ? (
                        <span className="px-2 py-0.5 bg-[#FF4D00] text-[#FDF0DF] font-cond uppercase text-[10px] tracking-[0.15em]">
                          {p.prize_type === "iphone" ? "🏆 iPhone" : `${p.prize_eur} €`}
                        </span>
                      ) : (
                        <span className="px-2 py-0.5 bg-[#14230A] text-[#FDF0DF] font-cond uppercase text-[10px] tracking-[0.15em]">
                          Perdu
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-xs">
                      {won && p.prize_type !== "iphone" ? (
                        p.proof_validated ? (
                          <span className="text-[#14230A] font-cond">✓ Vérifié</span>
                        ) : (
                          <span className="text-[#C53A1B] font-cond">à vérifier au stand</span>
                        )
                      ) : (
                        "—"
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function SponsorsB2B({ headers, token }) {
  const [data, setData] = useState({ sponsors: [], batches: [] });
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ sponsor_name: "", sponsor_email: "", amount_eur: 50, quantity: 10 });
  const [submitting, setSubmitting] = useState(false);
  const [lastBatch, setLastBatch] = useState(null);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  useEffect(() => {
    let cancelled = false;
    queueMicrotask(() => {
      if (cancelled) return;
      setLoading(true);
      axios
        .get(`${API}/admin/prepaid/sponsors`, { headers })
        .then(({ data }) => {
          if (!cancelled) setData(data);
        })
        .catch(() => {
          if (!cancelled) toast.error("Erreur chargement sponsors");
        })
        .finally(() => {
          if (!cancelled) setLoading(false);
        });
    });
    return () => {
      cancelled = true;
    };
  }, [headers, refreshTrigger]);

  const refresh = () => setRefreshTrigger((n) => n + 1);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.sponsor_name.trim()) {
      toast.error("Nom du sponsor requis");
      return;
    }
    setSubmitting(true);
    try {
      const { data: res } = await axios.post(`${API}/admin/prepaid/batch`, form, { headers });
      setLastBatch(res);
      toast.success(`${res.quantity} codes générés (${res.total_value_eur.toFixed(2)} €)`);
      setForm((f) => ({ ...f, sponsor_name: "", sponsor_email: "", quantity: 10 }));
      refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erreur");
    } finally {
      setSubmitting(false);
    }
  };

  const downloadPdf = (batchId) => {
    const url = `${API}/admin/prepaid/batch/${batchId}/pdf?token=${encodeURIComponent(token)}`;
    window.open(url, "_blank");
  };

  return (
    <div className="space-y-6">
      <div className="border-2 border-[#14230A] p-5">
        <div className="font-display text-2xl">Nouveau lot sponsor</div>
        <div className="font-cond uppercase text-[10px] tracking-[0.3em] opacity-60 mt-1">
          Génère des codes prépayés pour un sponsor B2B
        </div>
        <form onSubmit={onSubmit} className="mt-5 grid grid-cols-1 md:grid-cols-4 gap-3">
          <input
            data-testid="b2b-sponsor-name"
            value={form.sponsor_name}
            onChange={(e) => setForm((f) => ({ ...f, sponsor_name: e.target.value }))}
            placeholder="Nom sponsor"
            required
            className="h-11 px-3 border-2 border-[#14230A] bg-transparent font-cond"
          />
          <input
            data-testid="b2b-sponsor-email"
            type="email"
            value={form.sponsor_email}
            onChange={(e) => setForm((f) => ({ ...f, sponsor_email: e.target.value }))}
            placeholder="Email (optionnel)"
            className="h-11 px-3 border-2 border-[#14230A] bg-transparent font-cond"
          />
          <select
            data-testid="b2b-amount"
            value={form.amount_eur}
            onChange={(e) => setForm((f) => ({ ...f, amount_eur: parseInt(e.target.value) }))}
            className="h-11 px-3 border-2 border-[#14230A] bg-transparent font-cond"
          >
            <option value={25}>25 € / code</option>
            <option value={50}>50 € / code</option>
            <option value={100}>100 € / code</option>
          </select>
          <input
            data-testid="b2b-quantity"
            type="number"
            min={1}
            max={5000}
            value={form.quantity}
            onChange={(e) => setForm((f) => ({ ...f, quantity: parseInt(e.target.value) || 1 }))}
            placeholder="Quantité"
            className="h-11 px-3 border-2 border-[#14230A] bg-transparent font-cond"
          />
          <button
            data-testid="b2b-generate"
            type="submit"
            disabled={submitting}
            className="md:col-span-4 h-12 bg-[#FF4D00] text-[#FDF0DF] font-cond uppercase tracking-[0.2em] font-bold disabled:opacity-50 inline-flex items-center justify-center gap-2"
          >
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
            Générer · Total {(form.amount_eur * form.quantity).toFixed(2)} €
          </button>
        </form>
        {lastBatch && (
          <div className="mt-5 bg-[#14230A] text-[#FDF0DF] p-4 flex items-center justify-between gap-4 flex-wrap">
            <div>
              <div className="font-cond uppercase text-[10px] tracking-[0.3em] opacity-70">Dernier lot généré</div>
              <div className="font-display text-xl mt-1">{lastBatch.sponsor_name} · {lastBatch.quantity} × {lastBatch.amount_eur} €</div>
              <div className="text-xs opacity-70 mt-1">Réf {lastBatch.batch_id}</div>
            </div>
            <button
              data-testid="b2b-download-last"
              onClick={() => downloadPdf(lastBatch.batch_id)}
              className="h-11 px-4 bg-[#FF4D00] text-[#FDF0DF] font-cond uppercase tracking-[0.2em] text-xs font-bold inline-flex items-center gap-2"
            >
              <Download className="w-3.5 h-3.5" /> Télécharger PDF
            </button>
          </div>
        )}
      </div>

      <div className="border-2 border-[#14230A] overflow-x-auto">
        {loading ? (
          <div className="p-6"><Loader2 className="w-5 h-5 animate-spin" /></div>
        ) : data.sponsors.length === 0 ? (
          <div className="p-6 text-sm opacity-60 font-cond uppercase tracking-widest">
            Aucun lot sponsor pour l&apos;instant
          </div>
        ) : (
          <table className="w-full text-sm" data-testid="b2b-sponsors-table">
            <thead className="bg-[#14230A] text-[#FDF0DF] font-cond uppercase text-[10px] tracking-[0.2em]">
              <tr>
                <th className="text-left p-3">Sponsor</th>
                <th className="text-right p-3">Codes</th>
                <th className="text-right p-3">Alloué</th>
                <th className="text-right p-3">Consommé</th>
                <th className="text-right p-3">Restant</th>
                <th className="text-left p-3">Lots</th>
              </tr>
            </thead>
            <tbody>
              {data.sponsors.map((s) => (
                <tr key={s.sponsor_name} className="border-t border-[#14230A]/15 align-top">
                  <td className="p-3 font-bold">
                    {s.sponsor_name}
                    {s.sponsor_email && <div className="text-xs opacity-60">{s.sponsor_email}</div>}
                  </td>
                  <td className="p-3 text-right font-cond">{s.total_codes}</td>
                  <td className="p-3 text-right font-cond">{s.allocated_eur.toFixed(2)} €</td>
                  <td className="p-3 text-right font-cond text-[#C53A1B]">{s.consumed_eur.toFixed(2)} €</td>
                  <td className="p-3 text-right font-cond text-[#14230A]">{s.remaining_eur.toFixed(2)} €</td>
                  <td className="p-3 space-y-1">
                    {s.batches.map((b) => (
                      <div key={b.batch_id} className="flex items-center justify-between gap-2">
                        <span className="font-cond uppercase text-[10px] tracking-widest opacity-70">
                          {b.batch_id.split("-").pop()} · {b.count}×{b.amount_eur}€
                        </span>
                        <button
                          onClick={() => downloadPdf(b.batch_id)}
                          className="h-7 px-2 border-2 border-[#14230A] font-cond uppercase tracking-widest text-[10px] inline-flex items-center gap-1 hover:bg-[#FF4D00] hover:border-[#FF4D00] hover:text-[#FDF0DF]"
                        >
                          <Download className="w-3 h-3" /> PDF
                        </button>
                      </div>
                    ))}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}


